import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Lottie from 'lottie-react';
import api from '../api/client';
import { useAuthStore } from '../store/auth';

import buildingAnim from '../assets/animations/building.json';
import customerAnim from '../assets/animations/customer.json';
import projectAnim from '../assets/animations/project.json';
import successAnim from '../assets/animations/success.json';

type Step = 1 | 2 | 3;

const stepTitles = ['Firmendaten', 'Erster Kunde', 'Erstes Projekt'];
const stepAnims = [buildingAnim, customerAnim, projectAnim];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tenant = useAuthStore((s) => s.tenant);
  const [step, setStep] = useState<Step>(1);
  const [dir, setDir] = useState<'left' | 'right'>('left');
  const [animKey, setAnimKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCard, setShowCard] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const [company, setCompany] = useState({
    name: tenant?.name || '',
    address: { street: '', zip: '', city: '' },
    phone: '',
    taxId: '',
  });

  const [customer, setCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    address: { street: '', zip: '', city: '' },
  });

  const [project, setProject] = useState({ title: '', description: '' });

  useEffect(() => {
    const t = setTimeout(() => setShowCard(true), 100);
    return () => clearTimeout(t);
  }, []);

  const goTo = (next: Step) => {
    setDir(next > step ? 'left' : 'right');
    setAnimKey((k) => k + 1);
    setStep(next);
  };

  const submit = async () => {
    setError('');
    setLoading(true);
    try {
      const body: Record<string, unknown> = { company };
      if (customer.name) {
        body.customer = customer;
        if (project.title) body.project = project;
      }
      await api.post('/onboarding/complete', body);
      queryClient.setQueryData(['onboarding-status'], { completed: true });
      setShowSuccess(true);
      setTimeout(() => navigate('/'), 2200);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  const skip = async () => {
    setLoading(true);
    try {
      await api.post('/onboarding/skip');
      queryClient.setQueryData(['onboarding-status'], { completed: true });
      navigate('/');
    } catch {
      setError('Fehler beim Überspringen');
    } finally {
      setLoading(false);
    }
  };

  const slideClass = dir === 'left' ? 'animate-slide-left' : 'animate-slide-right';

  // Success overlay
  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-600 via-brand-500 to-blue-400 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-12 text-center animate-scale-in max-w-md">
          <Lottie animationData={successAnim} loop={false} className="w-40 h-40 mx-auto" />
          <h2 className="text-2xl font-bold text-gray-800 mt-4 animate-fade-in">Alles eingerichtet!</h2>
          <p className="text-gray-500 mt-2 animate-fade-in">Sie werden gleich weitergeleitet...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-600 via-brand-500 to-blue-400 flex items-center justify-center p-4">
      <div
        className={`bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden transition-all duration-500 ${
          showCard ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'
        }`}
      >
        {/* Header */}
        <div className="bg-gray-50 px-8 pt-6 pb-4 border-b">
          <div className="flex items-center gap-4 mb-4">
            {/* Lottie icon for current step */}
            <div className="w-16 h-16 flex-shrink-0">
              <Lottie
                key={step}
                animationData={stepAnims[step - 1]}
                loop
                className="w-16 h-16"
              />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Willkommen bei samoPlan</h1>
              <p className="text-gray-500 text-sm">
                Schritt {step} von 3 &mdash; {stepTitles[step - 1]}
              </p>
            </div>
          </div>

          {/* Step progress bar */}
          <div className="flex items-center gap-0">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-400 ${
                      s < step
                        ? 'bg-green-500 text-white'
                        : s === step
                        ? 'bg-brand-500 text-white scale-110 shadow-lg shadow-brand-500/30'
                        : 'bg-gray-200 text-gray-400'
                    }`}
                  >
                    {s < step ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      s
                    )}
                  </div>
                  <span className={`text-xs mt-1.5 transition-colors duration-300 ${
                    s === step ? 'text-brand-600 font-semibold' : 'text-gray-400'
                  }`}>
                    {stepTitles[s - 1]}
                  </span>
                </div>
                {s < 3 && (
                  <div className="w-full h-0.5 mx-2 mb-5 rounded overflow-hidden bg-gray-200">
                    <div
                      className={`h-full rounded transition-all duration-500 ease-out ${
                        s < step ? 'w-full bg-green-500' : 'w-0 bg-brand-500'
                      }`}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="px-8 py-6 min-h-[340px]">
          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm animate-fade-in">
              {error}
            </div>
          )}

          {/* Step 1: Company */}
          {step === 1 && (
            <div key={animKey} className={slideClass}>
              <h2 className="text-lg font-semibold mb-4">Ihre Firmendaten</h2>
              <div className="space-y-3">
                <InputField label="Firmenname *" value={company.name}
                  onChange={(v) => setCompany({ ...company, name: v })} autoFocus />
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <InputField label="Straße" value={company.address.street}
                      onChange={(v) => setCompany({ ...company, address: { ...company.address, street: v } })} />
                  </div>
                  <InputField label="PLZ" value={company.address.zip}
                    onChange={(v) => setCompany({ ...company, address: { ...company.address, zip: v } })} />
                </div>
                <InputField label="Ort" value={company.address.city}
                  onChange={(v) => setCompany({ ...company, address: { ...company.address, city: v } })} />
                <div className="grid grid-cols-2 gap-3">
                  <InputField label="Telefon" value={company.phone}
                    onChange={(v) => setCompany({ ...company, phone: v })} />
                  <InputField label="USt-IdNr." value={company.taxId}
                    onChange={(v) => setCompany({ ...company, taxId: v })} placeholder="DE123456789" />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: First customer */}
          {step === 2 && (
            <div key={animKey} className={slideClass}>
              <h2 className="text-lg font-semibold mb-1">Erster Kunde</h2>
              <p className="text-sm text-gray-500 mb-4">
                Legen Sie Ihren ersten Kunden an. Optional &ndash; kann auch später erledigt werden.
              </p>
              <div className="space-y-3">
                <InputField label="Kundenname" value={customer.name}
                  onChange={(v) => setCustomer({ ...customer, name: v })} autoFocus />
                <div className="grid grid-cols-2 gap-3">
                  <InputField label="E-Mail" value={customer.email}
                    onChange={(v) => setCustomer({ ...customer, email: v })} type="email" />
                  <InputField label="Telefon" value={customer.phone}
                    onChange={(v) => setCustomer({ ...customer, phone: v })} />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <InputField label="Straße" value={customer.address.street}
                      onChange={(v) => setCustomer({ ...customer, address: { ...customer.address, street: v } })} />
                  </div>
                  <InputField label="PLZ" value={customer.address.zip}
                    onChange={(v) => setCustomer({ ...customer, address: { ...customer.address, zip: v } })} />
                </div>
                <InputField label="Ort" value={customer.address.city}
                  onChange={(v) => setCustomer({ ...customer, address: { ...customer.address, city: v } })} />
              </div>
            </div>
          )}

          {/* Step 3: First project */}
          {step === 3 && (
            <div key={animKey} className={slideClass}>
              <h2 className="text-lg font-semibold mb-1">Erstes Projekt</h2>
              <p className="text-sm text-gray-500 mb-4">
                {customer.name
                  ? `Erstellen Sie ein erstes Projekt für "${customer.name}".`
                  : 'Sie haben keinen Kunden angelegt &ndash; schließen Sie die Einrichtung jetzt ab.'}
              </p>
              {customer.name ? (
                <div className="space-y-3">
                  <InputField label="Projektname" value={project.title}
                    onChange={(v) => setProject({ ...project, title: v })}
                    placeholder="z.B. Dachsanierung Musterstraße 5" autoFocus />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Beschreibung</label>
                    <textarea value={project.description}
                      onChange={(e) => setProject({ ...project, description: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 h-24 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-all"
                      placeholder="Kurze Beschreibung des Projekts..." />
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-8 text-center animate-scale-in">
                  <Lottie animationData={successAnim} loop={false} className="w-28 h-28 mx-auto" />
                  <p className="text-gray-600 mt-2">
                    Alles bereit! Klicken Sie auf "Einrichtung abschließen" um loszulegen.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-8 py-4 bg-gray-50 border-t flex justify-between items-center">
          <div>
            {step > 1 && (
              <button onClick={() => goTo((step - 1) as Step)}
                className="flex items-center gap-1 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-white hover:shadow-sm transition-all">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Zurück
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button onClick={skip} disabled={loading}
              className="px-4 py-2 text-sm text-gray-400 hover:text-gray-600 transition-colors">
              Überspringen
            </button>
            {step < 3 ? (
              <button
                onClick={() => {
                  if (step === 1 && !company.name) { setError('Bitte geben Sie einen Firmennamen ein'); return; }
                  setError('');
                  goTo((step + 1) as Step);
                }}
                className="flex items-center gap-1 bg-brand-500 text-white px-6 py-2 rounded-lg text-sm hover:bg-brand-600 hover:shadow-lg hover:shadow-brand-500/25 transition-all active:scale-95">
                Weiter
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
              <button onClick={submit} disabled={loading}
                className="flex items-center gap-2 bg-green-600 text-white px-6 py-2 rounded-lg text-sm hover:bg-green-700 hover:shadow-lg hover:shadow-green-600/25 transition-all active:scale-95 disabled:opacity-50">
                {loading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    Wird gespeichert...
                  </>
                ) : (
                  <>
                    Einrichtung abschließen
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function InputField({ label, value, onChange, type = 'text', placeholder, autoFocus }: {
  label: string; value: string; onChange: (v: string) => void;
  type?: string; placeholder?: string; autoFocus?: boolean;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-all"
        placeholder={placeholder} autoFocus={autoFocus} />
    </div>
  );
}
